import { ICareerSkillNode } from 'core/hooks/CareerSkillGraph/transform/transformCareerSkillGraph';

import { ISkillLayout, SkillMapDepth } from './skillGraphLayout';
import { ISkillGraphIndex } from './skillGraphModel';

/**
 * Какие группы карты раскрыты и куда смотреть первым (ТЗ 0071, п. 6.1).
 *
 * 🔴 Стартовый вид ВЫВОДИТСЯ, а не записывается в адрес. Первое открытие
 * (`?focus` и `?skill` пусты) показывает корень профессии, все группы и ОДНУ
 * раскрытую — с рекомендуемым узлом. Запиши его в адрес `replace`-ом — и
 * ссылка «на мою карту» навсегда застынет на сегодняшней рекомендации, хотя
 * завтра студент закроет этот пробел. Запиши `push`-ем — и «назад» с карты
 * будет вести на ту же карту.
 *
 * Правила собраны чистыми функциями по той же причине, что и раскладка:
 * «раскрыта не та группа» глазами ловится случайно, а тестом — одной строкой.
 */

/**
 * Рекомендуемый узел: первый ключевой пробел, иначе первый навык, не
 * дотянутый до требования, — в порядке показа графа (группы по порядку, узлы
 * внутри группы по порядку индекса).
 *
 * 🔴 Порядок берётся из ИНДЕКСА, а не из массива сервера: сервер свой
 * `ORDER BY` может сменить, и «следующий шаг» прыгал бы между релизами при
 * тех же данных.
 */
export const recommendedNode = (index: ISkillGraphIndex): ICareerSkillNode | null => {
  const ordered = index.clusters.flatMap((cluster) => index.nodesOf.get(cluster.code) || []);
  return (
    ordered.find((node) => node.status === 'missing_critical') ||
    ordered.find((node) => node.requirementStatus !== 'meets') ||
    null
  );
};

export interface IExpansionInput {
  depth: SkillMapDepth;
  /** Группа, раскрытая явно (клик, крошка, настройки). Уже проверена на существование. */
  focus: string | null;
  /** Группа выбранного навыка: выбранный узел обязан быть на карте. */
  selectedCluster: string | null;
  /** Группа рекомендуемого узла. */
  recommendedCluster: string | null;
  /** Первая группа карты — на случай, когда пробелов нет вовсе. */
  firstCluster: string | null;
  /** Активен отбор или поиск: найденное обязано быть видно. */
  showAll: boolean;
}

/**
 * Раскрытые группы. `null` — раскрыто всё (раскладка обрежет колонки).
 *
 * Порядок правил — это порядок силы намерения:
 * 1. явный фокус — человек сам выбрал группу;
 * 2. «Cała mapa» (глубина 1) — человек сам всё свернул;
 * 3. отбор или поиск — найденное в свёрнутой группе было бы невидимо, и
 *    «Znaleziono: 3» над пустым холстом читается как поломка;
 * 4. стартовый вид — ВСЕ группы свёрнуты (0072).
 *
 * 🔴 0072: раньше старт раскрывал группу рекомендуемого узла, и раскрытая
 * колонка из семи карточек выталкивала нижние группы за край холста — аудит:
 * «структуру приходится угадывать». Теперь первый вход — профессия и все её
 * группы; раскрывает группу клик, «Mój następny krok» или `?skill=`.
 *
 * Группа выбранного навыка добавляется ВСЕГДА: иначе чип «сначала нужно» в
 * паспорте открывал бы навык, которого на карте нет.
 */
export const expandedClusters = (input: IExpansionInput): string[] | null => {
  let base: string[];
  if (input.focus) base = [input.focus];
  else if (input.depth === 1) base = [];
  else if (input.showAll) return null;
  else base = [];
  if (input.selectedCluster && !base.includes(input.selectedCluster)) {
    base = [...base, input.selectedCluster];
  }
  return base;
};

/**
 * Карточки, которые стартовый вид обязан держать в поле зрения: корень
 * профессии, раскрытая группа и целевой узел (последним — он важнее, если все
 * три не помещаются).
 *
 * `null` у раскрытых — раскрыто всё, и якорем служит только выбранный узел:
 * корень посреди ленты из шестидесяти карточек в один экран не сведёшь.
 */
export const revealAnchors = (
  layout: ISkillLayout,
  expanded: string[] | null,
  target: string | null,
): string[] => {
  const keys: string[] = [];
  if (expanded && expanded.length) {
    keys.push('track', `cluster:${expanded[0]}`);
    const fallback = layout.cards.find(
      (card) => card.kind === 'skill' && card.parentKey === `cluster:${expanded[0]}`,
    );
    const aim = target && layout.byKey.has(target) ? target : fallback?.key;
    if (aim) keys.push(aim);
  } else if (target && layout.byKey.has(target)) {
    keys.push(target);
  }
  return keys.filter((key) => layout.byKey.has(key));
};
